import { lazy, Suspense, useEffect, type ReactNode } from 'react'
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api, auth, errorMessage } from './lib/api'
import { AppContext, useApp } from './context/AppContext'
import type { BootstrapData } from './types'
import AppShell from './components/AppShell'
import { ErrorState, Loading } from './components/ui'
import LoginPage from './pages/LoginPage'

const OverviewPage = lazy(() => import('./pages/OverviewPage'))
const LeadsPage = lazy(() => import('./pages/LeadsPage'))
const CompaniesPage = lazy(() => import('./pages/CompaniesPage'))
const CampaignsPage = lazy(() => import('./pages/CampaignsPage'))
const CampaignDetailPage = lazy(() => import('./pages/CampaignDetailPage'))
const CampaignEditorPage = lazy(() => import('./pages/CampaignEditorPage'))
const TemplatesPage = lazy(() => import('./pages/TemplatesPage'))
const TemplateEditorPage = lazy(() => import('./pages/TemplateEditorPage'))
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage'))
const TasksPage = lazy(() => import('./pages/TasksPage'))
const AdsPage = lazy(() => import('./pages/AdsPage'))
const DeliverabilityPage = lazy(() => import('./pages/DeliverabilityPage'))
const StudiesPage = lazy(() => import('./pages/StudiesPage'))
const EnrichmentPage = lazy(() => import('./pages/EnrichmentPage'))
const IntegrationsPage = lazy(() => import('./pages/IntegrationsPage'))
const AttributionPage = lazy(() => import('./pages/AttributionPage'))

const firstAllowedRoute = (permissions: string[]) => {
  if (permissions.includes('sponsorship.view')) return '/'
  if (permissions.includes('targeting.view')) return '/companies'
  if (permissions.includes('outreach.view')) return '/campaigns'
  if (permissions.includes('analytics.view')) return '/analytics'
  return null
}

function HomeRoute() {
  const app = useApp()
  if (app.permissions.includes('sponsorship.view')) return <OverviewPage />
  const route = firstAllowedRoute(app.permissions)
  return route ? <Navigate to={route} replace /> : <ErrorState message="لا توجد أقسام مصرح بها لهذا الحساب." />
}

function PermissionGate({ permission, children }: { permission: string; children: ReactNode }) {
  const app = useApp()
  return app.permissions.includes(permission)
    ? children
    : <ErrorState message="ليس لديك صلاحية لعرض هذا القسم." />
}

function ProtectedApp() {
  const navigate = useNavigate()
  useEffect(() => {
    const handler = () => navigate('/login')
    window.addEventListener('diraya:unauthorized', handler)
    return () => window.removeEventListener('diraya:unauthorized', handler)
  }, [navigate])
  const bootstrap = useQuery<BootstrapData>({
    queryKey: ['bootstrap'], queryFn: async () => (await api.get('/diraya3/bootstrap')).data,
    enabled: Boolean(auth.token()), staleTime: 300_000,
  })
  if (!auth.token()) return <Navigate to="/login" replace />
  if (bootstrap.isLoading) return <div className="boot-screen"><Loading label="يتم تجهيز غرفة العمليات..." /></div>
  if (bootstrap.isError || !bootstrap.data) return <div className="boot-screen"><ErrorState message={errorMessage(bootstrap.error)} /></div>
  return <AppContext.Provider value={bootstrap.data}><AppShell /></AppContext.Provider>
}

export default function App() {
  return <Suspense fallback={<div className="boot-screen"><Loading label="جارٍ فتح القسم..." /></div>}><Routes>
    <Route path="/login" element={<LoginPage />} />
    <Route element={<ProtectedApp />}>
      <Route index element={<HomeRoute />} />
      <Route path="leads" element={<PermissionGate permission="sponsorship.view"><LeadsPage /></PermissionGate>} />
      <Route path="companies" element={<PermissionGate permission="targeting.view"><CompaniesPage /></PermissionGate>} />
      <Route path="campaigns" element={<PermissionGate permission="outreach.view"><CampaignsPage /></PermissionGate>} />
      <Route path="campaigns/:id" element={<PermissionGate permission="outreach.view"><CampaignDetailPage /></PermissionGate>} />
      <Route path="campaigns/:id/edit" element={<PermissionGate permission="outreach.update"><CampaignEditorPage /></PermissionGate>} />
      <Route path="templates" element={<PermissionGate permission="outreach.view"><TemplatesPage /></PermissionGate>} />
      <Route path="templates/new" element={<PermissionGate permission="outreach.create"><TemplateEditorPage /></PermissionGate>} />
      <Route path="templates/:id/edit" element={<PermissionGate permission="outreach.update"><TemplateEditorPage /></PermissionGate>} />
      <Route path="analytics" element={<PermissionGate permission="analytics.view"><AnalyticsPage mode="full" /></PermissionGate>} />
      <Route path="outreach" element={<PermissionGate permission="outreach.view"><AnalyticsPage mode="outreach" /></PermissionGate>} />
      <Route path="tasks" element={<PermissionGate permission="sponsorship.view"><TasksPage /></PermissionGate>} />
      <Route path="ads" element={<PermissionGate permission="analytics.view"><AdsPage /></PermissionGate>} />
      <Route path="deliverability" element={<PermissionGate permission="outreach.view"><DeliverabilityPage /></PermissionGate>} />
      <Route path="studies" element={<PermissionGate permission="sponsorship.view"><StudiesPage /></PermissionGate>} />
      <Route path="enrichment" element={<PermissionGate permission="targeting.view"><EnrichmentPage /></PermissionGate>} />
      <Route path="integrations" element={<PermissionGate permission="analytics.view"><IntegrationsPage /></PermissionGate>} />
      <Route path="attribution" element={<PermissionGate permission="analytics.view"><AttributionPage /></PermissionGate>} />
    </Route>
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes></Suspense>
}
